import { PureComponent, ReactNode } from 'react';

export type ChildrenArgs = {
  items: any[];
  totalItems: number;
  toDisplay: number;
  toHide: number;
  displayMore: (e?: any) => unknown;
  displayLess: (e?: any) => unknown;
};

export type RollupProps = {
  /**
   * Number of items displayed on initial render
   */
  initialCount: number;
  /**
   * Complete array of items to display
   */
  items: any[];
  /**
   * Function which receives resulting state calculated by Rollup parent component.
   * It receives object with following properties:
   *
   * - **items**: sliced array of items to display
   * - **totalItems**: number of items in initial list
   * - **toDisplay**: number of hidden items
   * - **toHide**: number of items which will hide when going back to initial state
   * - **displayMore**: function which can be called to reveal more items
   * - **displayLess**: function which can be called to hide items and return to initial state
   *
   */
  children: (args: ChildrenArgs) => ReactNode;
  /**
   * Number of items to reveal each time `displayMore` is called
   */
  step: number;
  /**
   * Index of item which must be visible. If needed number of initially rendered items will be
   * modified to ensure list is expanded to display the required item.
   */
  ensureVisibleItemIndex?: number;
};

type State = {
  interactionHappened: boolean;
  visibleItemsCount: number;
};

export class Rollup extends PureComponent<RollupProps, State> {
  public static displayName = 'Rollup';

  public static defaultProps = {
    items: [],
    ensureVisibleItemIndex: undefined
  };

  public constructor(props: RollupProps) {
    super(props);

    let forcedSteps = 0;
    if (props.ensureVisibleItemIndex) {
      const minimumItemsVisible = Math.max(props.initialCount, props.ensureVisibleItemIndex + 1);

      while (props.initialCount + forcedSteps * props.step < minimumItemsVisible) {
        forcedSteps++;
      }
    }

    this.state = {
      interactionHappened: false,
      visibleItemsCount: Math.min(props.items.length, props.initialCount + forcedSteps * props.step)
    };
  }

  public static getDerivedStateFromProps(
    props: RollupProps, state: State, nextItems = props.items, nextInitialCount = props.initialCount
  ) {
    if (nextItems.length !== props.items.length) {
      return {
        visibleItemsCount: Math.min(state.visibleItemsCount, nextItems.length)
      };
    }

    if (!state.interactionHappened && nextInitialCount !== props.initialCount) {
      return {
        visibleItemsCount: Math.min(nextInitialCount, nextItems.length)
      };
    }

    return null;
  }

  /**
   * @public
   */
  public resetInteraction() {
    this.setState({
      interactionHappened: false
    });
  }

  public render() {
    const {
      props: { children, items, initialCount },
      state: { visibleItemsCount }
    } = this;

    return children({
      items: items.slice(0, visibleItemsCount),
      totalItems: items.length,
      toDisplay: items.length - visibleItemsCount,
      toHide: visibleItemsCount - initialCount,
      displayMore: (e: Event) => {
        if (e) {
          e.preventDefault();
        }
        this.setState(({ visibleItemsCount: currentVisibleItemsCount }, { items: currentItems, step }) => ({
          interactionHappened: true,
          visibleItemsCount: Math.min(currentVisibleItemsCount + step, currentItems.length)
        }));
      },
      displayLess: (e: Event) => {
        if (e) {
          e.preventDefault();
        }

        this.setState({
          interactionHappened: true,
          visibleItemsCount: initialCount
        });
      }
    });
  }
}
